import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { NewsArticleNumList } from '@/features/actualidad-hostelera/NewsArticleNumList'
import { createNewsArticleSourceChip } from '@/features/actualidad-hostelera/NewsArticleSourceChip'
import type { NewsArticleBodyProps } from '@/features/actualidad-hostelera/articles'
import { toOfficialSalesWhatsAppHref } from '@/lib/officialContact'
import { cn } from '@/lib/utils'
import { article } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import '@/styles/food-cost-fonts.css'

const k = (f: string) => `news.articles.rentabilidad-hosteleria-20-anos.${f}`

const REF = {
    aeat: {
        href: 'https://sede.agenciatributaria.gob.es/Sede/estadisticas/observatorio-margenes-empresariales/observatorio-margenes-empresariales.html',
        domain: 'agenciatributaria.gob.es',
        label: 'AEAT',
    },
    bde: {
        href: 'https://www.bde.es/f/webbe/SES/Secciones/Publicaciones/PublicacionesAnuales/CentralBalances/24/Fich/ceba24.pdf',
        domain: 'bde.es',
        label: 'Banco de España',
    },
    ine: {
        href: 'https://www.ine.es/',
        domain: 'ine.es',
        label: 'INE',
    },
    smi: {
        href: 'https://www.boe.es/buscar/doc.php?id=BOE-A-2026-3815',
        domain: 'boe.es',
        label: 'BOE · SMI 2026',
    },
    anuario: {
        href: 'https://hosteleriadeespana.es/publicaciones-hosteleria.html',
        domain: 'hosteleriadeespana.es',
        label: 'Anuario Hostelería de España',
    },
} as const

const SourceChip = createNewsArticleSourceChip(REF, k)

export function RentabilidadHosteleriaArticle({ article: meta }: NewsArticleBodyProps) {
    const { t } = useTranslation()

    const contactWhatsapp = toOfficialSalesWhatsAppHref(
        t(
            k('contactWhatsapp'),
            'Hola, he leído el artículo de rentabilidad en hostelería y me gustaría ver dónde se me va el margen.',
        ),
    )

    const weekActions: ReactNode[] = [
        <>
            <strong>{t(k('weekActions.1.strong'), 'Calcula tu EBITDA real de los últimos 12 meses.')}</strong>
            {t(
                k('weekActions.1.after'),
                ' Ventas menos compras menos personal menos gastos de estructura. Sin tu sueldo dentro. Es el número que no tienes.',
            )}
        </>,
        <>
            <strong>{t(k('weekActions.2.strong'), 'Escandalla los cinco platos que más vendes.')}</strong>
            {t(k('weekActions.2.after'), ' No los que más te gustan: los que más salen. Ahí está el 70% de tu margen.')}
        </>,
        <>
            <strong>{t(k('weekActions.3.strong'), 'Saca la lista de gastos que en 2006 no existían.')}</strong>
            {t(
                k('weekActions.3.after'),
                ' Súmalos. Ese es tu coste de estar al día, y probablemente nunca lo has mirado junto.',
            )}
        </>,
        <>
            <strong>{t(k('weekActions.4.strong'), 'Compara la subida de tu carta con la de tu proveedor')}</strong>
            {t(
                k('weekActions.4.after'),
                ' en los últimos tres años. Si tu carta va por detrás, ya sabes dónde se ha ido el margen.',
            )}
        </>,
        <>
            <strong>{t(k('weekActions.5.strong'), 'Pon fecha a Verifactu en el calendario')}</strong>
            {t(
                k('weekActions.5.after'),
                ' y pregunta hoy a tu proveedor de TPV si su software cumple. Los que esperen a diciembre pagarán más.',
            )}
        </>,
    ]

    const sources = [
        {
            label: t(k('sources.1'), 'Observatorio de márgenes empresariales, Agencia Tributaria'),
            href: REF.aeat.href,
        },
        {
            label: t(k('sources.2'), 'Central de Balances 2024, Banco de España'),
            href: REF.bde.href,
        },
        {
            label: t(k('sources.3'), 'Instituto Nacional de Estadística, IPC y estadísticas de hostelería'),
            href: REF.ine.href,
        },
        {
            label: t(k('sources.4'), 'Real Decreto 126/2026 (SMI 2026), BOE'),
            href: REF.smi.href,
        },
        {
            label: t(
                k('sources.5'),
                'Estudio del precio del menú del día y Anuario de la Hostelería de España, Hostelería de España',
            ),
            href: REF.anuario.href,
        },
        {
            label: t(
                k('sources.6'),
                'Real Decreto 1007/2023 (Verifactu) y Ley 11/2021 de medidas de prevención y lucha contra el fraude fiscal',
            ),
            href: 'https://www.boe.es/buscar/act.php?id=BOE-A-2023-24840',
        },
        {
            label: t(k('sources.7'), 'Real Decreto-ley 8/2019 (registro horario obligatorio)'),
            href: 'https://www.boe.es/buscar/act.php?id=BOE-A-2019-3481',
        },
    ]

    return (
        <div className={article.page}>
            <article className={article.shell}>
                <ArticleCoverHero
                    category={meta.category}
                    title={meta.title}
                    dek={
                        <>
                            {t(k('dek.beforeMenu'), 'El menú del día ha subido un ')}
                            <strong>{t(k('dek.menuPct'), '19,5%')}</strong>
                            {t(k('dek.afterMenuBeforeFood'), ' desde 2016. La comida que va dentro de ese menú, un ')}
                            <strong>{t(k('dek.foodPct'), '39,2%')}</strong>
                            {t(
                                k('dek.afterFood'),
                                '. Mientras tanto, la Agencia Tributaria dice que el margen empresarial español marcó récord en 2023: un 12,8% sobre ventas. Las dos cosas son ciertas a la vez. Y juntas explican la paradoja de la rentabilidad en hostelería: por qué tu bar deja menos que el de tu padre justo cuando las estadísticas dicen que el sector gana más.',
                            )}
                        </>
                    }
                    imageSrc={meta.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Caja registradora antigua junto a un datáfono moderno sobre la barra de un bar',
                    )}
                    byline={
                        <ArticleByline
                            authorName={meta.authorName}
                            readingMinutes={meta.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <div className={article.proseIntro}>
                    <p className={article.p}>
                        {t(
                            k('intro.1'),
                            'Piensa en el bar de hace veinte años. Barra de madera, carta de dos folios, tres personas en plantilla y una caja registradora que abría de un tirón.',
                        )}
                    </p>
                    <p className={article.p}>
                        {t(
                            k('intro.2'),
                            'El dueño no tenía software, ni escandallo, ni asesor de datos. Tenía una libreta y buena memoria.',
                        )}
                    </p>
                    <p className={article.p}>{t(k('intro.3'), 'Y a final de año le quedaba dinero.')}</p>
                    <p className={article.p}>
                        {t(
                            k('intro.4'),
                            'Hoy tú tienes TPV certificado, fichajes, APPCC, seguros, comisiones de tarjeta y un asesor al que pagas todos los meses. Y a final de año no te queda ni la mitad.',
                        )}
                    </p>
                    <p className={cn(article.p, article.pGap2xl)}>
                        {t(
                            k('intro.5'),
                            'Lo raro no es que te pase. Lo raro es que las estadísticas oficiales digan justo lo contrario.',
                        )}
                    </p>
                </div>

                <section id='en-cristiano'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.enCristiano'), 'Los márgenes de Hacienda frente a tu caja')}
                    </h2>
                    <div className={article.tableWrap}>
                        <table className={article.tableFacts}>
                            <tbody>
                                <tr>
                                    <th scope='row'>
                                        <span className={article.cite}>
                                            {t(k('table.margen.label'), 'Margen empresarial 2023')}
                                            <SourceChip source='aeat' />
                                        </span>
                                    </th>
                                    <td>
                                        <strong>{t(k('table.margen.value'), '12,8%')}</strong>
                                        {t(k('table.margen.after'), ' sobre ventas, récord desde 2009')}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>
                                        <span className={article.cite}>
                                            {t(k('table.rentabilidad.label'), 'Rentabilidad comercio y hostelería')}
                                            <SourceChip source='bde' />
                                        </span>
                                    </th>
                                    <td>
                                        {t(k('table.rentabilidad.before'), 'sube del ')}
                                        <strong>{t(k('table.rentabilidad.from'), '9,9%')}</strong>
                                        {t(k('table.rentabilidad.mid'), ' al ')}
                                        <strong>{t(k('table.rentabilidad.to'), '13,4%')}</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>
                                        {t(k('table.menu.label'), 'Precio del menú del día 2016 → 2024')}
                                    </th>
                                    <td>
                                        <strong>{t(k('table.menu.value'), '+19,5%')}</strong>
                                        {t(k('table.menu.after'), ' (de 11,7 € a 14 €)')}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>
                                        <span className={article.cite}>
                                            {t(k('table.ipc.label'), 'IPC de alimentos en ese mismo periodo')}
                                            <SourceChip source='ine' />
                                        </span>
                                    </th>
                                    <td>
                                        <strong>{t(k('table.ipc.value'), '+39,2%')}</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>
                                        <span className={article.cite}>
                                            {t(k('table.smi.label'), 'Salario mínimo 2006 → 2026')}
                                            <SourceChip source='smi' />
                                        </span>
                                    </th>
                                    <td>
                                        {t(k('table.smi.before'), 'de ')}
                                        <strong>{t(k('table.smi.from'), '540,90 €')}</strong>
                                        {t(k('table.smi.mid'), ' a ')}
                                        <strong>{t(k('table.smi.to'), '1.221 €')}</strong>
                                        {t(k('table.smi.after'), ': un +126%')}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>
                                        <span className={article.cite}>
                                            {t(k('table.bares.label'), 'Bares en España 2010 → hoy')}
                                            <SourceChip source='anuario' />
                                        </span>
                                    </th>
                                    <td>
                                        {t(k('table.bares.before'), 'de ')}
                                        <strong>{t(k('table.bares.from'), '202.720')}</strong>
                                        {t(k('table.bares.mid'), ' a unos ')}
                                        <strong>{t(k('table.bares.to'), '164.000')}</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className={article.prose}>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(k('table.conclusion.before'), 'La conclusión práctica cabe en una línea: ')}
                            <strong>
                                {t(
                                    k('table.conclusion.strong'),
                                    'tus precios han subido la mitad de rápido que tus compras, y esa diferencia la has puesto tú de tu margen.',
                                )}
                            </strong>
                        </p>
                    </div>
                </section>

                <section id='datos-oficiales'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(
                            k('h2.datosOficiales'),
                            '¿Por qué los datos oficiales dicen que la rentabilidad en hostelería está en máximos?',
                        )}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(k('oficiales.p1'), 'Porque miden lo que se declara, y eso hoy no se parece a 2006.')}
                        </p>
                        <p className={article.p}>
                            {t(
                                k('oficiales.p3'),
                                'Buena parte de aquellos márgenes del 25 o el 30% no pasaba por la caja. No es un juicio moral: es contabilidad. Hostelería, comercio y construcción concentran irregularidades detectadas por Hacienda.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            <strong>
                                {t(
                                    k('oficiales.p6'),
                                    'El margen oficial mejora en parte porque el margen real se ha hecho visible, no porque haya crecido.',
                                )}
                            </strong>{' '}
                            {t(
                                k('oficiales.note'),
                                'Es nuestra lectura: no hay serie oficial que separe afloramiento de crecimiento real.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='mala-noticia'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.malaNoticia'), 'Suelo, sueldos y Verifactu: lo que se come el margen')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            <strong>{t(k('mala.suelo.strong'), 'El suelo.')}</strong>{' '}
                            {t(k('mala.suelo.after'), 'Alquiler por IPC y competencia de usos que pagan más.')}
                        </p>
                        <p className={article.p}>
                            <strong>{t(k('mala.sueldos.strong'), 'Los sueldos.')}</strong>{' '}
                            {t(k('mala.sueldos.before'), 'El ')}
                            <span className={article.cite}>
                                {t(k('mala.sueldos.cite'), 'SMI')}
                                <SourceChip source='smi' />
                            </span>{' '}
                            {t(
                                k('mala.sueldos.after'),
                                'de 540,90 € (2006) a 1.221 € (2026): +126%. Materias primas +39,2% desde 2016, más del doble que tu menú.',
                            )}
                        </p>
                        <p className={article.p}>
                            {t(
                                k('mala.capa'),
                                'Y gastos que en 2006 no existían: registro horario (mayo 2019), APPCC, comisiones de tarjeta, seguros, software certificado.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            <strong>
                                {t(
                                    k('mala.verifactu'),
                                    'Verifactu: sociedades el 1 de enero de 2027; resto de autónomos el 1 de julio de 2027.',
                                )}
                            </strong>{' '}
                            {t(
                                k('mala.multas'),
                                'Multas hasta 50.000 € por ejercicio si el programa no cumple. Ninguno de esos gastos trae un cliente: todos salen de tu margen.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='cuenta-vieja'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.cuentaVieja'), 'Para ganar lo mismo, factura 3,6 veces más')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(k('cuenta.p2.before'), 'Un euro de 2006 son hoy ~1,45 € ')}
                            <em>{t(k('cuenta.p2.note'), '(estimación propia con la serie IPC del INE)')}</em>
                            {t(k('cuenta.p3.before'), '. Con un EBITDA del 25% entonces y del 10% hoy, ')}
                            <strong>{t(k('cuenta.p3.strong'), 'necesitas facturar unas 3,6 veces más')}</strong>{' '}
                            <em>{t(k('cuenta.p3.note'), '(estimación propia)')}</em>
                            {t(
                                k('cuenta.p4'),
                                ': más del doble de clientes en el mismo local. Por eso trabajas más y ganas menos.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='bar-300000'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.bar300'), 'Un bar de 300.000 euros, en 2006 y en 2026')}
                    </h2>
                    <div className={article.compare}>
                        <div className={article.compareCard}>
                            <p className={article.compareLabel}>{t(k('bar.2006.label'), '2006')}</p>
                            <p className={article.compareTitle}>{t(k('bar.2006.title'), 'Facturas 300.000 €')}</p>
                            <p className={article.compareBody}>
                                {t(k('bar.2006.before'), 'EBITDA del 25%: te quedan ')}
                                <strong>{t(k('bar.2006.ebitda'), '75.000 €')}</strong>
                                {t(k('bar.2006.after'), '. En dinero de hoy, unos 109.000 €.')}
                            </p>
                        </div>
                        <div className={article.compareCard}>
                            <p className={article.compareLabelAlert}>{t(k('bar.2026.label'), '2026')}</p>
                            <p className={article.compareTitle}>{t(k('bar.2026.title'), 'Facturas 435.000 €')}</p>
                            <p className={article.compareBody}>
                                {t(
                                    k('bar.2026.before'),
                                    'El mismo bar, con los precios actualizados. EBITDA del 10%: te quedan ',
                                )}
                                <strong>{t(k('bar.2026.ebitda'), '43.500 €')}</strong>.
                            </p>
                        </div>
                    </div>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(k('bar.outro.before'), 'Mismo local. Más plantilla, más normativa, más horas. ')}
                            <strong>{t(k('bar.outro.strong'), 'Menos de la mitad de beneficio real.')}</strong>
                        </p>
                        <p className={cn(article.pItalic, article.pGap2xl)}>
                            {t(
                                k('bar.note'),
                                'Ejemplo construido con supuestos propios. No es un caso real de ningún cliente.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='era-real'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.eraReal'), 'Entonces, ¿aquel 30% era real o estaba sobredimensionado?')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            <strong>{t(k('real.p2.strong'), 'Era real para quien lo cobraba.')}</strong>{' '}
                            {t(
                                k('real.p3.after'),
                                'Y sobredimensionado como retrato del sector: parte no tributaba y faltaban costes que hoy son obligatorios. Lo que no vuelve es el colchón: en 2006 un bar mediocre daba de vivir; en 2026 cierra. Por eso hay 38.000 bares menos que en 2010. El sector se regulariza y se profesionaliza; con Verifactu, la puerta del efectivo no se reabre.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='esta-semana'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.estaSemana'), 'Tres números que miraría esta semana')}
                    </h2>
                    <div className={article.prose}>
                        <NewsArticleNumList items={weekActions} />
                    </div>
                </section>

                <section id='pendientes'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.pendientes'), 'Lo que Hacienda no desglosa (y Verifactu puede moverse)')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            <strong>
                                {t(
                                    k('pendientes.p1.strong'),
                                    'No hay cifra oficial de economía sumergida desglosada solo para hostelería.',
                                )}
                            </strong>{' '}
                            {t(
                                k('pendientes.p1.after'),
                                'Existen estimaciones del conjunto de la economía española, pero atribuir a la hostelería un porcentaje concreto sería inventarlo. Nuestra tesis del afloramiento es una lectura razonada, no un dato.',
                            )}
                        </p>
                        <p className={article.p}>
                            <strong>
                                {t(
                                    k('pendientes.p2.strong'),
                                    'Los agregados de la AEAT y del Banco de España mezclan hoteles, cadenas y bares de barrio.',
                                )}
                            </strong>{' '}
                            {t(
                                k('pendientes.p2.after'),
                                'Un hotel de costa y un bar de menús no viven la misma película, y el mismo dato dice cosas opuestas según a quién mires.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            <strong>
                                {t(k('pendientes.p3.strong'), 'Las fechas de Verifactu ya se han movido una vez.')}
                            </strong>{' '}
                            {t(k('pendientes.p3.after'), 'Pueden volver a moverse.')}
                        </p>
                    </div>
                </section>

                <div className={article.prose}>
                    <p className={cn(article.p, article.pGap2xl)}>
                        {t(
                            k('closing.p1'),
                            'Si trabajas más y te quedas con menos, la respuesta está en saber plato a plato dónde se te va el dinero. Empieza por tu EBITDA de los últimos 12 meses y el escandallo de tus cinco platos top.',
                        )}
                    </p>
                </div>

                <section className={article.cta}>
                    <h2 className={cn(article.h2, article.h2Cta)}>
                        {t(k('h2.cta'), '¿Sabes dónde se te va el margen?')}
                    </h2>
                    <p className={article.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Revisamos tu EBITDA, el escandallo de tus platos top y la subida de tu carta frente a la del proveedor.',
                        )}
                    </p>
                    <div className={article.ctaActionsCenter}>
                        <a
                            href={contactWhatsapp}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(article.btn, article.btnSolid)}
                        >
                            {t(k('cta.whatsapp'), 'Escríbenos por WhatsApp')}
                        </a>
                    </div>
                </section>

                <section className={article.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={article.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={article.sourcesList}>
                        {sources.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={article.link}
                                >
                                    {source.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
